import { SearchLg } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { ProgressBar } from "@/components/base/progress-indicators/progress-indicators";
import { Input } from "@/components/base/input/input";
import { useTranslation } from "react-i18next";
import { getDocumentStatusColor } from "./world-map-utils";
import type { WorldMapRoute } from "./world-map-utils";

export interface WorldMapListPanelProps {
    isOpen: boolean;
    onClose: () => void;
    searchTerm: string;
    onSearchChange: (value: string) => void;
    routes: WorldMapRoute[];
    progressById: Record<string, number>;
    hoveredContainerId: string | null;
    highlightedRouteId: string | null;
    onContainerClick: (route: WorldMapRoute) => void;
    onContainerHover: (containerId: string | null) => void;
    translateDocumentStatus: (status: string) => string;
}

export function WorldMapListPanel({
    isOpen,
    onClose,
    searchTerm,
    onSearchChange,
    routes,
    progressById,
    hoveredContainerId,
    highlightedRouteId,
    onContainerClick,
    onContainerHover,
    translateDocumentStatus,
}: WorldMapListPanelProps) {
    const { t } = useTranslation("pages:containers");

    return (
        <div
            className={`absolute inset-y-0 left-0 z-[1000] bg-primary_alt shadow-xl transition-all duration-300 overflow-hidden flex flex-col ${isOpen ? 'w-[400px]' : 'w-0'}`}
        >
            {isOpen && (
                <div className="flex flex-col h-full w-full">
                    <div className="relative px-6 pt-6 pb-4 border-b border-secondary_alt flex-shrink-0 space-y-3">
                        <div>
                            <h3 className="text-lg font-medium text-brand-primary">{t('worldMap.containerList')}</h3>
                            <p className="mt-0.5 text-xs text-tertiary">
                                {routes.length} {routes.length === 1 ? t('worldMap.containerSingular') : t('worldMap.containerPlural')}
                            </p>
                        </div>
                        <Input
                            placeholder={t('worldMap.searchPlaceholder', { defaultValue: 'Search container, carrier...' })}
                            value={searchTerm}
                            onChange={onSearchChange}
                            icon={SearchLg}
                            size="sm"
                            className="w-full"
                        />
                        <button
                            onClick={onClose}
                            className="absolute top-3 right-3 text-tertiary hover:text-primary transition-colors"
                            type="button"
                        >
                            <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto px-4 md:px-6 pb-6">
                        {routes.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 px-4">
                                <div className="rounded-full bg-secondary p-4 mb-4">
                                    <svg className="size-10 text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.447 2.724A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                    </svg>
                                </div>
                                <h4 className="text-md font-semibold text-primary mb-1">{t('worldMap.emptyState.title', 'Nessun container con posizione')}</h4>
                                <p className="text-sm text-tertiary text-center max-w-xs">
                                    {t('worldMap.emptyState.description', 'I container in questo periodo non hanno ancora dati di tracciamento disponibili o non sono ancora partiti')}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3 pt-4">
                                {routes.map((route) => {
                                    const { c, color } = route;
                                    const progress = Math.round((progressById[c.id] ?? 0) * 100);
                                    const isHovered = hoveredContainerId === c.id;
                                    const isSelected = highlightedRouteId === c.id;

                                    return (
                                        <div
                                            key={c.id}
                                            role="button"
                                            tabIndex={0}
                                            className={`rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary cursor-pointer transition-all ${isHovered || isSelected ? 'ring-2 shadow-md' : 'hover:ring-2 hover:shadow-sm'}`}
                                            style={{ borderLeft: `4px solid ${color}` }}
                                            onMouseEnter={() => onContainerHover(c.id)}
                                            onMouseLeave={() => onContainerHover(null)}
                                            onClick={() => onContainerClick(route)}
                                            onKeyDown={(e) => e.key === 'Enter' && onContainerClick(route)}
                                        >
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex-1">
                                                    <div className="flex items-center justify-between mb-1">
                                                        <h4 className="text-md font-semibold text-primary">{c.containerNumber}</h4>
                                                        <Badge color={getDocumentStatusColor(c.documentStatus)} size="sm">
                                                            Docs Status: {translateDocumentStatus(c.documentStatus)}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-sm text-tertiary">
                                                        {c.route.from} → {c.route.to}
                                                    </p>
                                                    <p className="text-xs text-tertiary mt-1">{c.shippingCompany ?? ""}</p>
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-tertiary">Progress</span>
                                                    <span className="text-primary font-medium">{progress}%</span>
                                                </div>
                                                <ProgressBar value={progress} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
